import React, { useEffect, useState } from 'react';
import { Select } from './UI';
import { CompanyProfile, CrmBalances, Project, Transaction } from '../types';
import { api } from '../services/api';
import { formatMoney, orgDisplayName } from '../utils/format';

const LAST_ORG_KEY = 'lastOrgId';

// Организация в форме: выбранная в шапке (заблокирована), организация операции при правке,
// иначе — последняя использованная. rememberOrg() запоминает выбор после сохранения
export const useOrgChoice = (active: boolean, orgs: CompanyProfile[], fixedOrgId: string | null,
                             editing?: Transaction | null): [string, (v: string) => void, () => void] => {
    const [orgId, setOrgId] = useState('');
    useEffect(() => {
        if (!active) return;
        let last = '';
        try { last = localStorage.getItem(LAST_ORG_KEY) || ''; } catch { /* нет доступа к хранилищу */ }
        setOrgId(editing?.organizationId || fixedOrgId || (orgs.some(o => o.id === last) ? last : orgs[0]?.id || ''));
    }, [active, editing, fixedOrgId, orgs]);
    const remember = () => { try { localStorage.setItem(LAST_ORG_KEY, orgId); } catch { /* не критично */ } };
    return [orgId, setOrgId, remember];
};

// Сколько должны людям по CRM — при каждом открытии окна выплаты свежие числа
export const useCrmBalances = (active: boolean): CrmBalances | null => {
    const [data, setData] = useState<CrmBalances | null>(null);
    useEffect(() => {
        if (!active) return;
        let live = true;
        setData(null);
        api.crmSalaryBalances().then(d => { if (live) setData(d); })
            .catch(() => { if (live) setData({ available: false, error: 'Не удалось получить остатки из CRM', month: null, items: {} }); });
        return () => { live = false; };
    }, [active]);
    return data;
};

const MONTH_NOM = ['январь', 'февраль', 'март', 'апрель', 'май', 'июнь', 'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь'];
const MONTH_PREP = ['январе', 'феврале', 'марте', 'апреле', 'мае', 'июне', 'июле', 'августе', 'сентябре', 'октябре', 'ноябре', 'декабре'];
const MONTH_GEN = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];

// Подсказка «сколько должны»: долг на конец прошлого месяца, что уже выплачено в этом, начислено за этот и итоговый остаток.
// compact — строка в списке людей: если человека нет в CRM или CRM не ответила, ничего не показывает.
export const CrmDebtHint: React.FC<{ balances: CrmBalances | null; counterpartyId: string; compact?: boolean }> = ({ balances, counterpartyId, compact }) => {
    const cls = compact ? 'basis-full text-[11px] text-slate-600' : 'rounded border border-blue-100 bg-blue-50 px-2 py-1.5 text-xs text-slate-700 space-y-0.5';
    if (!counterpartyId) return null;
    if (!balances) return compact ? null : <div className={cls}>Смотрю в CRM, сколько должны…</div>;
    if (!balances.available) return compact ? null : <div className={cls}>{balances.error}</div>;
    const b = balances.items[counterpartyId];
    if (!b) return compact ? null : <div className={cls}>Этого человека нет в CRM — подсказать долг нельзя.</div>;
    if (b.excluded) return <div className={cls}>В расчёте зарплаты CRM не участвует.</div>;
    const m = Number((balances.month || '').slice(5)) - 1;
    const prev = (m + 11) % 12;
    return (
        <div className={cls}>
            <div>
                {b.previousBalance > 0
                    ? <>За {MONTH_NOM[prev]} и раньше должны {b.paid > 0 ? formatMoney(b.previousBalance) : <strong>{formatMoney(b.previousBalance)}</strong>}</>
                    : b.previousBalance < 0 ? <>Переплата на конец {MONTH_GEN[prev]}: <strong>{formatMoney(-b.previousBalance)}</strong></>
                    : <>За {MONTH_NOM[prev]} долга нет</>}
                {b.paid > 0 && <> · в {MONTH_PREP[m]} выплачено {formatMoney(b.paid)}</>}
                {b.paid > 0 && b.previousBalance > 0 && <> · осталось <strong>{formatMoney(b.previousLeft)}</strong></>}
            </div>
            {!!b.toPay && <div className="text-slate-500">За {MONTH_NOM[m]} на сегодня {formatMoney(b.toPay)} · всего остаток {formatMoney(b.remaining || 0)}</div>}
        </div>
    );
};

export const OrgSelect: React.FC<{ orgs: CompanyProfile[]; value: string; onChange: (v: string) => void; locked: boolean }> =
    ({ orgs, value, onChange, locked }) => (
    <Select label="Организация" value={value} disabled={locked} onChange={e => onChange(e.target.value)}
        options={orgs.map(o => ({ value: o.id, label: orgDisplayName(o) }))} />
);

// Вид выплаты в форме «Зарплата»: штат (с налогами), неофициально (обычно наличными), самозанятый (чек «Мой налог»)
export type PayKind = 'SALARY' | 'UNOFFICIAL_SALARY' | 'SELF_EMPLOYED';
const PAY_KINDS: [PayKind, string][] = [['SALARY', 'Штат (официально)'], ['UNOFFICIAL_SALARY', 'Неофициально (наличные)'], ['SELF_EMPLOYED', 'Самозанятый']];

export const PayKindTabs: React.FC<{ value: PayKind; onChange: (v: PayKind) => void; withSelfEmployed?: boolean }> =
    ({ value, onChange, withSelfEmployed = true }) => (
    <div className="grid grid-cols-3 rounded-md border border-slate-300 overflow-hidden text-xs">
        {PAY_KINDS.filter(([k]) => withSelfEmployed || k !== 'SELF_EMPLOYED').map(([k, l]) => (
            <button key={k} type="button" onClick={() => onChange(k)}
                className={`px-2 py-2 font-medium border-r last:border-r-0 border-slate-300 ${value === k ? 'bg-red-500 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}>{l}</button>
        ))}
    </div>
);

// alsoClosed — закрытые объекты, которые всё равно можно выбрать (в выплате самозанятому — с остатком квоты ИП)
export const ProjectSelect: React.FC<{ projects: Project[]; value: string; onChange: (v: string) => void; alsoClosed?: string[] }> =
    ({ projects, value, onChange, alsoClosed }) => projects.length === 0 ? null : (
    <Select label="Объект (необязательно)" value={value} onChange={e => onChange(e.target.value)}
        options={[{ value: '', label: 'Без объекта' },
                  ...projects.filter(p => p.status !== 'CLOSED' || p.id === value || alsoClosed?.includes(p.id))
                      .map(p => ({ value: p.id, label: p.status === 'CLOSED' ? `${p.name} (закрыт, есть объём для выплат)` : p.name }))]} />
);
